import React from 'react';
import type { Dispatch } from 'redux';
import memoizeOne from 'memoize-one';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { ComponentWithAnalytics } from '@atlassian/jira-analytics-web-react';
import componentWithCondition from '@atlassian/jira-common-util-component-with-condition';
import { ff } from '@atlassian/jira-feature-flagging';
import { fg } from '@atlassian/jira-feature-gating';
import CommentVisibilitySelect from '@atlassian/jira-issue-comment-base/src/ui/comment/comment-visibility';
import { EventOccurredAtSelector } from '@atlassian/jira-issue-comment-base/src/ui/comment/event-occurred-at-selector';
import { useIsIssueOfIncidentsPractice } from '@atlassian/jira-issue-field-servicedesk-practices';
import {
	COMMENT_VISIBILITY_PUBLIC,
	COMMENT_VISIBILITY_TYPE_PUBLIC,
	type CommentVisibility,
} from '@atlassian/jira-issue-view-common-types/src/comment-type';
import type { State } from '@atlassian/jira-issue-view-common-types/src/issue-type';
import { flowWithSafeComponent } from '@atlassian/jira-issue-view-common-utils';
import { findMentionsInAdfContent } from '@atlassian/jira-issue-view-common-utils/src/mentions';
import withContainerWidth from '@atlassian/jira-issue-view-common-utils/src/with-container-width';
import { connect } from '@atlassian/jira-issue-view-react-redux';
import {
	addCommentFormCancel,
	addCommentFormCollapse,
	addCommentFormExpand,
	editCommentUpdate,
	editCommentVisibility,
	pastedIntoCommentForm,
	saveCommentRequest,
	type CommentSaveRequest,
	editCommentEventOccurredAt,
	type EditCommentVisibilityAction,
} from '@atlassian/jira-issue-view-store/src/actions/comment-actions';
import {
	accountIdloggedInUserSelector,
	isServiceDeskSelector,
	isMobileSelector,
} from '@atlassian/jira-issue-view-store/src/common/state/selectors/context-selector';
import { isRichTextFieldEditingSelector } from '@atlassian/jira-issue-view-store/src/common/state/selectors/field-selector';
import {
	loggedInUserAvatarUrlSelector,
	loggedInUserDisplayNameSelector,
	projectTypeSelector,
} from '@atlassian/jira-issue-view-store/src/common/state/selectors/issue-selector';
import {
	canUseServiceDeskAgentFeaturesSelector,
	canAdministerProjectPermissionsSelector,
	canAdministerJiraPermissionsSelector,
} from '@atlassian/jira-issue-view-store/src/common/state/selectors/permissions-selector';
import { createCommentDraftKey } from '@atlassian/jira-issue-view-store/src/drafts/draft-utils';
import { getSelectedActivitySortOrder } from '@atlassian/jira-issue-view-store/src/selectors/activity-feed-selector';
import { fullIssueUrlSelector } from '@atlassian/jira-issue-view-store/src/selectors/breadcrumbs-selector';
import { NEW_COMMENT_ID } from '@atlassian/jira-issue-view-store/src/selectors/comment-constants';
import {
	commentEditingBodySelector,
	commentEditingOrViewBodyAdfSelector,
	commentEditingVisibilitySelector,
	isAddCommentEditorExpandedSelector,
	isCommentAttachmentInProgressSelector,
	isCommentVisibilityRestrictionSupportedSelector,
	isEditingAnyCommentSelector,
	commentEditingEventOccurredAtSelector,
	isEditingInternalCommentSelector,
	addCommentEditorForceFocusSelector,
} from '@atlassian/jira-issue-view-store/src/selectors/comment-selector';
import { fieldHasDraftSelector } from '@atlassian/jira-issue-view-store/src/selectors/drafts/selectors';
import { fireOperationalAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { functionWithFFRedux } from '@atlassian/jira-react-redux';
import type { ADF } from '@atlassian/jira-rich-content';
import { isJsmTimelineEnabled, isCannedResponseFFEnabled } from '../feature-flags';
import { AddCommentForm } from './view';

const isCannedResponseEnabled = (state: State) => {
	const isServiceDesk = isServiceDeskSelector(state);
	const isMobile = isMobileSelector(state);
	const isAdminOrAgent =
		canAdministerJiraPermissionsSelector(state) ||
		canAdministerProjectPermissionsSelector(state) ||
		canUseServiceDeskAgentFeaturesSelector(state);
	return isCannedResponseFFEnabled() && isServiceDesk && !isMobile && isAdminOrAgent;
};

const isSmartRepliesEnabled = (state: State) => {
	if (fg('smart_replies_m1_kill_switch')) {
		const projectType = projectTypeSelector(state);
		return projectType === 'software' || projectType === 'business';
	}
	return false;
};

const mapStateToProps = (state: State) => ({
	authorId: accountIdloggedInUserSelector(state),
	avatarUrl: loggedInUserAvatarUrlSelector(state),
	displayName: loggedInUserDisplayNameSelector(state),
	newCommentDraft: commentEditingOrViewBodyAdfSelector(NEW_COMMENT_ID)(state),
	hasDraft: fieldHasDraftSelector(createCommentDraftKey(NEW_COMMENT_ID))(state),
	// We should expand if we have explicitly requested expansion
	// or if we have currently have a draft
	isExpanded:
		isAddCommentEditorExpandedSelector(state) ||
		commentEditingBodySelector(NEW_COMMENT_ID)(state) != null,
	forceFocus: addCommentEditorForceFocusSelector(state),
	isServiceDesk: isServiceDeskSelector(state),
	isEditorFocused: isRichTextFieldEditingSelector(state) || isEditingAnyCommentSelector(state),
	isLoading: isServiceDeskSelector(state) && isCommentAttachmentInProgressSelector(state),
	shouldShowCommentVisibilitySelect: isCommentVisibilityRestrictionSupportedSelector(state),
	visibility: commentEditingVisibilitySelector(NEW_COMMENT_ID)(state) || COMMENT_VISIBILITY_PUBLIC,
	sortOrder: getSelectedActivitySortOrder(state),
	eventOccurredAt: commentEditingEventOccurredAtSelector(NEW_COMMENT_ID)(state) || null,
	isEditingInternal: isEditingInternalCommentSelector(NEW_COMMENT_ID)(state),
	fullIssueUrl: fullIssueUrlSelector(state),
	isCannedResponseEnabled: isCannedResponseEnabled(state),
	isSmartRepliesEnabled: isSmartRepliesEnabled(state),
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
	onExpand: () => dispatch(addCommentFormExpand()),
	onEdit: (value: ADF) => dispatch(editCommentUpdate(NEW_COMMENT_ID, value)),
	onVisibilityChange: (visibility: CommentVisibility) =>
		dispatch(editCommentVisibility(NEW_COMMENT_ID, visibility)),
	onPaste: (pastedContent: string) =>
		dispatch(pastedIntoCommentForm(NEW_COMMENT_ID, pastedContent)),
	onBlur: () => dispatch(addCommentFormCollapse()),
	onCancel: () => dispatch(addCommentFormCancel()),
	onEventOccurredAtChange: (eventOccurredAt: number | null) => {
		dispatch(editCommentEventOccurredAt(NEW_COMMENT_ID, eventOccurredAt));
	},
	getOnSave: (
		eventData: Omit<CommentSaveRequest, 'id' | 'createdDate'>,
		analyticsEvent: UIAnalyticsEvent,
	) =>
		dispatch(
			saveCommentRequest(
				// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
				{
					id: `${NEW_COMMENT_ID}_${new Date().getTime()}`,
					createdDate: new Date().toISOString(),
					isNewComment: true,
					...eventData,
				} as CommentSaveRequest,
				// eslint-disable-next-line @typescript-eslint/consistent-type-assertions, @typescript-eslint/no-explicit-any
				analyticsEvent as any,
			),
		),
});

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;
type OwnProps = { isIssueOfIncidentsPractice?: boolean };
const mergePropsOld = (
	stateProps: StateProps,
	dispatchProps: DispatchProps,
	ownProps: OwnProps,
) => {
	const jsmTimelineComponents =
		isJsmTimelineEnabled() && stateProps.isEditingInternal
			? [
					<EventOccurredAtSelector
						key="eventOccurredAtSelector"
						initialValue={stateProps.eventOccurredAt}
						onChange={(eventOccurredAt: number | null) => {
							dispatchProps.onEventOccurredAtChange(eventOccurredAt);
						}}
					/>,
				]
			: [];

	const shouldShowCommentVisibility = ff('jsm-restrict-internal-comment-visibility')
		? stateProps.shouldShowCommentVisibilitySelect && stateProps.isEditingInternal
		: stateProps.shouldShowCommentVisibilitySelect;

	return {
		...ownProps,
		...stateProps,
		...dispatchProps,
		editorSecondaryToolbarComponents: shouldShowCommentVisibility
			? [
					...jsmTimelineComponents,
					<CommentVisibilitySelect
						key="commentVisibilitySelect"
						initialValue={stateProps.visibility}
						onChange={dispatchProps.onVisibilityChange}
					/>,
				]
			: jsmTimelineComponents,
		onSave: (value: ADF, analyticsEvent: UIAnalyticsEvent) => {
			if (ff('sea-2350-analytics-for-mentions-in-board-and-backlog_2gkji')) {
				if (value?.content) {
					const mentions = findMentionsInAdfContent(value.content);
					analyticsEvent.update({
						mentionedUserIds: mentions.map((mention) => mention.attrs.id),
					});
				}
			}

			stateProps.eventOccurredAt
				? analyticsEvent.update({
						isEventOccurredAtSelected: true,
					})
				: analyticsEvent.update({
						isEventOccurredAtSelected: false,
					});
			if (stateProps.shouldShowCommentVisibilitySelect) {
				analyticsEvent.update({
					isCommentRestricted: stateProps.visibility.type !== COMMENT_VISIBILITY_TYPE_PUBLIC,
				});
			}

			dispatchProps.getOnSave(
				{
					authorId: stateProps.authorId,
					bodyAdf: value,
					bodyHtml: undefined,
					richContent: undefined,
					visibility: stateProps.visibility,

					...(isJsmTimelineEnabled() &&
					ownProps.isIssueOfIncidentsPractice &&
					stateProps.isEditingInternal
						? {
								eventOccurredAt: stateProps.eventOccurredAt,
								jsdIncidentActivityViewHidden: false,
								fullIssueUrl: stateProps.fullIssueUrl,
								triggerIncidentSaveCommentFlag: Boolean(
									ownProps.isIssueOfIncidentsPractice && stateProps.eventOccurredAt,
								),
							}
						: {}),
				},
				analyticsEvent,
			);
		},
		onEditorExpandedWithAnalytics: (analyticsEvent: UIAnalyticsEvent) => {
			dispatchProps.onExpand();
			fireOperationalAnalytics(
				// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
				analyticsEvent as UIAnalyticsEvent,
				'addCommentEditor expanded',
				{
					hasDraft: stateProps.hasDraft,
				},
			);
		},
	};
};

const editorSecondaryToolbarComponentsFactory = memoizeOne(
	(
		shouldShowCommentVisibility: boolean,
		visibility: CommentVisibility,
		onVisibilityChange: (visibility: CommentVisibility) => EditCommentVisibilityAction,
		jsmTimelineComponents: React.JSX.Element[],
	) =>
		shouldShowCommentVisibility
			? [
					...jsmTimelineComponents,
					<CommentVisibilitySelect
						key="commentVisibilitySelect"
						initialValue={visibility}
						onChange={onVisibilityChange}
					/>,
				]
			: jsmTimelineComponents,
);

const onSaveFactory = memoizeOne(
	(
		eventOccurredAt: StateProps['eventOccurredAt'],
		shouldShowCommentVisibilitySelect: StateProps['shouldShowCommentVisibilitySelect'],
		visibility: CommentVisibility,
		getOnSave: DispatchProps['getOnSave'],
		authorId: StateProps['authorId'],
		isIssueOfIncidentsPractice: OwnProps['isIssueOfIncidentsPractice'],
		isEditingInternal: StateProps['isEditingInternal'],
		fullIssueUrl: StateProps['fullIssueUrl'],
	) =>
		(value: ADF, analyticsEvent: UIAnalyticsEvent) => {
			if (ff('sea-2350-analytics-for-mentions-in-board-and-backlog_2gkji')) {
				if (value?.content) {
					const mentions = findMentionsInAdfContent(value.content);
					analyticsEvent.update({
						mentionedUserIds: mentions.map((mention) => mention.attrs.id),
					});
				}
			}

			eventOccurredAt
				? analyticsEvent.update({
						isEventOccurredAtSelected: true,
					})
				: analyticsEvent.update({
						isEventOccurredAtSelected: false,
					});
			if (shouldShowCommentVisibilitySelect) {
				analyticsEvent.update({
					isCommentRestricted: visibility.type !== COMMENT_VISIBILITY_TYPE_PUBLIC,
				});
			}

			getOnSave(
				{
					authorId,
					bodyAdf: value,
					bodyHtml: undefined,
					richContent: undefined,
					visibility,

					...(isJsmTimelineEnabled() && isIssueOfIncidentsPractice && isEditingInternal
						? {
								eventOccurredAt,
								jsdIncidentActivityViewHidden: false,
								fullIssueUrl,
								triggerIncidentSaveCommentFlag: Boolean(
									isIssueOfIncidentsPractice && eventOccurredAt,
								),
							}
						: {}),
				},
				analyticsEvent,
			);
		},
);

const onEditorExpandedWithAnalyticsFactory = memoizeOne(
	(onExpand: DispatchProps['onExpand'], hasDraft: StateProps['hasDraft']) =>
		(analyticsEvent: UIAnalyticsEvent) => {
			onExpand();
			fireOperationalAnalytics(
				// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
				analyticsEvent as UIAnalyticsEvent,
				'addCommentEditor expanded',
				{
					hasDraft,
				},
			);
		},
);

const jsmTimelineComponentsFactory = memoizeOne(
	(
		isJsmTimelineEnabledFF: boolean,
		isEditingInternal: StateProps['isEditingInternal'],
		statePropsEventOccurredAt: StateProps['eventOccurredAt'],
		onEventOccurredAtChange: DispatchProps['onEventOccurredAtChange'],
	) =>
		isJsmTimelineEnabledFF && isEditingInternal
			? [
					<EventOccurredAtSelector
						key="eventOccurredAtSelector"
						initialValue={statePropsEventOccurredAt}
						onChange={(eventOccurredAt: number | null) => {
							onEventOccurredAtChange(eventOccurredAt);
						}}
					/>,
				]
			: [],
);
const mergePropsNew = (
	stateProps: StateProps,
	dispatchProps: DispatchProps,
	ownProps: OwnProps,
) => {
	const jsmTimelineComponents = jsmTimelineComponentsFactory(
		isJsmTimelineEnabled(),
		stateProps.isEditingInternal,
		stateProps.eventOccurredAt,
		dispatchProps.onEventOccurredAtChange,
	);

	const shouldShowCommentVisibility = ff('jsm-restrict-internal-comment-visibility')
		? stateProps.shouldShowCommentVisibilitySelect && stateProps.isEditingInternal
		: stateProps.shouldShowCommentVisibilitySelect;

	return {
		...ownProps,
		...stateProps,
		...dispatchProps,
		editorSecondaryToolbarComponents: editorSecondaryToolbarComponentsFactory(
			shouldShowCommentVisibility,
			stateProps.visibility,
			dispatchProps.onVisibilityChange,
			jsmTimelineComponents,
		),
		onSave: onSaveFactory(
			stateProps.eventOccurredAt,
			stateProps.shouldShowCommentVisibilitySelect,
			stateProps.visibility,
			dispatchProps.getOnSave,
			stateProps.authorId,
			ownProps.isIssueOfIncidentsPractice,
			stateProps.isEditingInternal,
			stateProps.fullIssueUrl,
		),
		onEditorExpandedWithAnalytics: onEditorExpandedWithAnalyticsFactory(
			dispatchProps.onExpand,
			stateProps.hasDraft,
		),
	};
};

export const ConnectedAddCommentForm = flowWithSafeComponent(
	withContainerWidth,
	ComponentWithAnalytics('issueFields', {
		onSave: 'created',
		onEditorExpandedWithAnalytics: 'commentExpanded',
	}),
	// @ts-expect-error - Argument of type 'InferableComponentEnhancerWithProps<{ editorSecondaryToolbarComponents: Element[]; onSave: (value: DocNode, analyticsEvent: UIAnalyticsEvent) => void; onEditorExpandedWithAnalytics: (analyticsEvent: UIAnalyticsEvent) => void; ... 25 more ...; isIssueOfIncidentsPractice?: boolean | undefined; }, { ...; }>' is not assignable to parameter of type 'FlowStep<FC<Omit<{ [K in keyof PropsWithRef<Omit<P, "containerWidth">>]: K extends "onSave" | "onEditorExpandedWithAnalytics" ? (...args: [...Parameters<PropsWithRef<Omit<P, "containerWidth">>[K]>, UIAnalyticsEvent]) => ReturnType<...> : PropsWithRef<...>[K]; }, keyof WithAnalyticsEventsProps>>, ConnectedComponent<....'.
	connect(mapStateToProps, mapDispatchToProps, functionWithFFRedux(mergePropsNew, mergePropsOld)),
)(AddCommentForm);

export const ConnectedAddCommentFormWithIssueOfIncidentsPractice = () => {
	const isIssueOfIncidentsPractice = useIsIssueOfIncidentsPractice();

	return <ConnectedAddCommentForm isIssueOfIncidentsPractice={isIssueOfIncidentsPractice} />;
};

export default componentWithCondition(
	isJsmTimelineEnabled,
	ConnectedAddCommentFormWithIssueOfIncidentsPractice,
	ConnectedAddCommentForm,
);
